import React, { useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import { RED_COLORS_VARIANTS } from '@/utils/constants/colors'
import { padTime } from '@/utils/formatters'

dayjs.extend(utc)
dayjs.extend(duration)

interface GameCountdownProps {
  startTime: string | undefined
  isInProgress: boolean
  status?: 'UPCOMING' | 'ACTIVE' | 'FINISHED' | string
  iconSize?: number
  fontSize?: number
  label?: string
  showLivePill?: boolean
}

function GameCountdown({
  startTime,
  isInProgress,
  status,
  iconSize = 16,
  fontSize = 16,
  label,
  showLivePill = false,
}: GameCountdownProps) {
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0, diff: Infinity })
  const liveStatusColor = '#EF4444'
  const timeColor = '#64645B'
  const defaultTimeColor = '#353535'

  useEffect(() => {
    if (!startTime) return

    const update = () => {
      const diff = dayjs.utc(startTime).diff(dayjs.utc())

      if (diff <= 0) {
        setCountdown({ hours: 0, minutes: 0, seconds: 0, diff: 0 })
        return
      }

      const d = dayjs.duration(diff)
      setCountdown({
        hours: d.hours(),
        minutes: d.minutes(),
        seconds: d.seconds(),
        diff,
      })
    }
    update()
    const interval = setInterval(update, 1_000)
    return () => clearInterval(interval)
  }, [startTime])

  const isLive = isInProgress
  const isUnder24h = countdown.diff > 0 && countdown.diff <= 24 * 60 * 60 * 1000
  const getCountdownText = () => {
    if (status === 'FINISHED') return 'FINISHED'
    if (isLive) return ' '
    if (!startTime) return ' '

    if (countdown.diff > 24 * 60 * 60 * 1000) {
      return dayjs.utc(startTime).local().format('D MMM HH:mm A')
    }
    const { hours, minutes, seconds } = countdown
    return `${padTime(hours)} : ${padTime(minutes)} : ${padTime(seconds)}`
  }

  const statusColor = (() => {
    if (status === 'FINISHED' || countdown.diff === 0) return timeColor
    if (isLive || isUnder24h) return liveStatusColor
    return defaultTimeColor
  })()

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {status === 'UPCOMING' && (
          <>
            <AccessTimeIcon sx={{ fontSize: iconSize, color: timeColor }} />
            <Typography sx={{ fontSize, color: timeColor }}>
              {label || 'Game starts in: '}
            </Typography>
          </>
        )}
      </Box>

      {isLive && showLivePill ? (
        <Box
          display="flex"
          alignItems="center"
          gap="5px"
          px={1.25}
          py={0.4}
          borderRadius="999px"
          fontSize="0.75rem"
          fontWeight={700}
          bgcolor={RED_COLORS_VARIANTS[250]}
          color={liveStatusColor}
          border={RED_COLORS_VARIANTS[300]}
          sx={{ letterSpacing: '0.05em' }}
        >
          <Box
            width="7px"
            height="7px"
            borderRadius="50%"
            bgcolor={liveStatusColor}
            sx={{
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          LIVE
        </Box>
      ) : (
        <Typography
          sx={{
            fontSize,
            fontWeight: 700,
            color: statusColor,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {getCountdownText()}
        </Typography>
      )}
    </Box>
  )
}

export default GameCountdown
